import { useCalendarEvents } from "../hooks/useCalendarEvents";
import { ModuleHeader } from "../../../shared/components/ModuleHeader";
import { CalendarIcon } from "../../../shared/components/icons";
import styles from "./CalendarModule.module.css";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m} min` : `${h}h`;
}

interface CalendarModuleProps {
  readonly graphToken: string | null;
  readonly onBack?: () => void;
}

export function CalendarModule({ graphToken, onBack }: Readonly<CalendarModuleProps>) {
  const { events, loading, error, conflictIndices } = useCalendarEvents(graphToken);
  const today = new Date();
  const dateLabel = `Today · ${DAY_NAMES[today.getDay()]} ${today.getDate()} ${MONTH_NAMES[today.getMonth()]}`;

  if (!graphToken) {
    return (
      <div className={styles.module}>
        <ModuleHeader
          title="Calendar Management"
          subtitle="Connect Microsoft 365 to view calendar"
          icon={CalendarIcon}
          accent="calendar"
          onBack={onBack}
        />
        <div className={styles.empty}>Sign in to access your calendar.</div>
      </div>
    );
  }

  const subtitle = loading ? "Loading…" : dateLabel;

  return (
    <div className={styles.module}>
      <ModuleHeader
        title="Calendar Management"
        subtitle={subtitle}
        icon={CalendarIcon}
        accent="calendar"
        onBack={onBack}
      />

      {error && <div className={styles.error}>{error}</div>}

      {!loading && events.length === 0 && !error && (
        <div className={styles.empty}>No events today.</div>
      )}

      <div className={styles.list}>
        {events.map((ev, idx) => {
          const start = new Date(ev.start.dateTime + "Z");
          const end = new Date(ev.end.dateTime + "Z");
          const timeStr = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
          const durationStr = formatDuration(minutes);
          const isConflict = conflictIndices.has(idx);
          const loc = ev.location?.displayName ?? "";

          return (
            <div key={ev.id} className={styles.calSlot}>
              <div className={styles.calTime}>{timeStr}</div>
              <div className={`${styles.calEv} ${isConflict ? styles.calEvConflict : ""}`}>
                <div className={styles.calEvTitle}>
                  {isConflict ? "⚠ " : ""}
                  {ev.subject || "(no title)"}
                </div>
                <div className={styles.calEvMeta}>
                  {loc ? `${loc} · ` : ""}
                  {durationStr}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
